import styles from './truncated-text.module.css'

import cn from 'clsx'
import React, { useEffect, useState } from 'react'

import { Text, Tooltip } from 'components/ds'

type Props = {
  children: string
}

/**
 * Text that truncates to a single line with an ellipsis, revealing the full
 * value in a tooltip only when it actually overflows. The tooltip opens on tap
 * as well as hover, since touch devices have no hover and the ellipsis would
 * otherwise hide the value for good.
 */
const TruncatedText = ({ children }: Props) => {
  // Held in state, not a ref: wrapping in the tooltip remounts the element, and
  // the effect has to re-subscribe to the new node rather than keep measuring
  // the detached one.
  const [textNode, setTextNode] = useState<HTMLSpanElement | null>(null)
  const [isTruncated, setIsTruncated] = useState(false)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  useEffect(() => {
    if (!textNode) return

    const measure = () =>
      setIsTruncated(textNode.scrollWidth > textNode.clientWidth)

    measure()

    // The column width tracks the modal, so re-measure on resize too.
    const observer = new ResizeObserver(measure)
    observer.observe(textNode)

    return () => observer.disconnect()
  }, [textNode, children])

  if (!isTruncated) {
    return (
      <Text ref={setTextNode} size="2" truncate className={styles.text}>
        {children}
      </Text>
    )
  }

  return (
    <Tooltip
      content={children}
      open={isTooltipOpen}
      onOpenChange={setIsTooltipOpen}
    >
      <Text
        ref={setTextNode}
        size="2"
        truncate
        className={cn(styles.text, styles.revealable)}
        // Touch has no hover, so a clipped value needs the tap to reveal it.
        // Swallowing the click keeps the row and cell click targets underneath
        // from also firing; anchors still navigate, since their destination
        // answers the tap better than the tooltip does.
        onClick={event => {
          event.stopPropagation()
          setIsTooltipOpen(open => !open)
        }}
      >
        {children}
      </Text>
    </Tooltip>
  )
}

export default React.memo<Props>(TruncatedText)
